import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, Search, ShieldCheck, Users } from 'lucide-react';

import { request } from '@/api/client';
import type {
  ActivityBand,
  AdminTeam,
  Page,
  Person,
  PersonRole,
  SubscriptionStatus,
} from '@/api/types';
import { ActivityBadge, ActivityLegend } from '@/components/ActivityBadge';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Select,
  TableSkeleton,
  TextInput,
  Toggle,
} from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { countryFlag, countryName, date, relativeDays } from '@/lib/format';
import { useDebounced, useResource, useSort } from '@/lib/hooks';


export function PeoplePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { account } = useAuth();

  const [role, setRole] = useState<PersonRole | 'all'>('all');
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityBand | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Person | null>(null);
  const [assigning, setAssigning] = useState<Person | null>(null);
  const [subscribing, setSubscribing] = useState<Person | null>(null);
  const [closing, setClosing] = useState<Person | null>(null);
  const [promoting, setPromoting] = useState<Person | null>(null);
  const [typedEmail, setTypedEmail] = useState('');
  const { sort, order, toggle } = useSort('created_at');
  const debouncedSearch = useDebounced(search);
  const [pageSize, setPageSize] = usePageSize('people');

  const { data, error, loading, reload } = useResource<Page<Person>>(
    '/api/v1/admin/people',
    {
      role,
      search: debouncedSearch,
      activity: activity || undefined,
      sort,
      order,
      page,
      page_size: pageSize,
    },
    [role, debouncedSearch, activity, sort, order, page, pageSize],
  );

  const language = i18n.language;
  const filtered = role !== 'all' || debouncedSearch !== '' || activity !== '';

  // Erasure. Typed confirmation rather than a second button: this one takes
  // a month to undo and cannot be undone at all once the month is up.
  async function closeAccount(person: Person) {
    try {
      await request(`/api/v1/admin/people/${person.id}/erasure`, { method: 'POST' });
      toast.success(t('people.erasureRequested'));
      setClosing(null);
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  async function reopenAccount(person: Person) {
    try {
      await request(`/api/v1/admin/people/${person.id}/erasure`, { method: 'DELETE' });
      toast.success(t('people.erasureCancelled'));
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  // Asked first, both ways. This hands over -- or takes away -- every screen
  // in the dashboard, and it is the one action here that used to happen on a
  // single click while closing an account asks for a typed e-mail address.
  async function toggleAdmin(person: Person) {
    const grant = !person.is_super_admin;
    try {
      await request(`/api/v1/admin/people/${person.id}/super-admin`, {
        method: 'POST',
        query: { grant },
      });
      toast.success(t(grant ? 'people.adminGranted' : 'people.adminRevoked'));
      setPromoting(null);
      reload();
    } catch {
      toast.failure(t('people.cannotRevokeSelf'));
    }
  }

  const columns: Column<Person>[] = [
    {
      key: 'name',
      header: t('people.name'),
      sortKey: 'name',
      render: (person) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-ink">
              {person.real_name || person.display_name}
            </span>
            {person.is_super_admin ? (
              <ShieldCheck
                aria-label={t('people.adminAccess')}
                className="h-3.5 w-3.5 shrink-0 text-brand"
              />
            ) : null}
          </div>
          <div className="truncate text-2xs text-ink-subtle">{person.email}</div>
          {/* Row 105: outside their squad this athlete is their nickname and
              their code, and those are the two strings somebody reads out on
              the phone. A twelfth column would be past the fold. */}
          {person.nickname || person.player_code ? (
            <div className="truncate text-2xs text-ink-subtle">
              {[person.nickname, person.player_code].filter(Boolean).join(' · ')}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'role',
      header: t('people.role'),
      sortKey: 'role',
      render: (person) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge tone={person.role === 'coach' ? 'info' : 'neutral'}>
            {t(`people.role${cap(person.role)}`)}
          </Badge>
          {/* Only when it is not the app: marking the ordinary case would put
              a badge on every row and say nothing. */}
          {person.registration_source && person.registration_source !== 'app' ? (
            <Badge tone="neutral">
              {t(`people.source.${person.registration_source}`, {
                defaultValue: person.registration_source,
              })}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      // Third from the left on purpose. It is the column the aftersales work
      // is done from, and with eleven columns the table scrolls sideways --
      // anything past the fold may as well not be there.
      key: 'activity',
      header: t('people.activity'),
      sortKey: 'last_seen_at',
      render: (person) => (
        <div className="flex items-center gap-2">
          <ActivityBadge band={person.activity} />
          <span className="text-2xs text-ink-subtle">
            {relativeDays(person.last_seen_at, language) ?? ''}
          </span>
        </div>
      ),
    },
    {
      key: 'ageGroup',
      header: t('people.ageGroup'),
      sortKey: 'birth_date',
      render: (person) => (
        <span className="text-ink-muted">
          {person.age_group ?? <Missing />}
        </span>
      ),
    },
    {
      key: 'gender',
      header: t('people.gender'),
      sortKey: 'gender',
      render: (person) =>
        person.gender ? (
          <span className="text-ink-muted">{t(`people.gender${cap(person.gender)}`)}</span>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'position',
      header: t('people.position'),
      sortKey: 'position',
      render: (person) =>
        person.field_position ? (
          <span className="text-ink-muted">
            {t(`people.positions.${person.field_position}`, {
              defaultValue: person.field_position,
            })}
          </span>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'country',
      header: t('people.country'),
      sortKey: 'country',
      render: (person) => {
        const code = person.nationality ?? person.country;
        if (!code) return <Missing />;
        return (
          <span className="text-ink-muted">
            <span aria-hidden>{countryFlag(code)} </span>
            {countryName(code, language)}
          </span>
        );
      },
    },
    {
      key: 'club',
      header: t('people.club'),
      sortKey: 'club',
      render: (person) =>
        person.club_name ? (
          <div className="min-w-0">
            <div className="truncate text-ink-muted">{person.club_name}</div>
            {person.teams.length ? (
              <div className="truncate text-2xs text-ink-subtle">
                {person.teams.map((team) => team.name).join(', ')}
              </div>
            ) : null}
          </div>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'subscription',
      header: t('people.subscription'),
      sortKey: 'status',
      render: (person) => (
        <Badge tone={person.subscription_status === 'vip' ? 'info' : 'neutral'}>
          {t(`subscription.${person.subscription_status}`)}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: t('people.createdAt'),
      sortKey: 'created_at',
      align: 'right',
      render: (person) => (
        <span className="text-ink-subtle">{date(person.created_at, language)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('people.adminAccess'),
      align: 'right',
      render: (person) => (
        <div className="flex justify-end gap-2">
          <Button onClick={() => setEditing(person)} className="text-xs">
            {t('people.baseData')}
          </Button>
          {!person.erased_at && !person.erasure_requested_at && person.role !== 'parent' && <Button
            onClick={() => navigate(`/marketing-consents?athlete=${person.id}`)} className="text-xs">
            {t('marketing.personAction')}
          </Button>}
          {/*
            Row 103: a VIP has to be revocable again, and somebody who never
            redeemed a code has to be able to become one. Finanzen cannot do
            either -- it lists subscriptions, and a person who has never had
            one is not in it. This is the list that holds everybody.
          */}
          <Button onClick={() => setSubscribing(person)} className="text-xs">
            {t('people.subscription')}
          </Button>
          {/*
            Row 110 is about VIPs, and the endpoint answers for nobody else.
            Offering the button on every row would be offering a button that
            fails on all but a handful of them.
          */}
          {person.subscription_status === 'vip' ? (
            <Button onClick={() => setAssigning(person)} className="text-xs">
              {t('people.team')}
            </Button>
          ) : null}
          <Button
            onClick={() => setPromoting(person)}
            disabled={person.id === account?.id}
            className="text-xs"
          >
            {t(person.is_super_admin ? 'people.revokeAdmin' : 'people.grantAdmin')}
          </Button>
          {person.erased_at ? null : person.erasure_requested_at ? (
            <Button onClick={() => reopenAccount(person)} className="text-xs">
              {t('people.erasureCancel')}
            </Button>
          ) : (
            <Button
              onClick={() => setClosing(person)}
              disabled={person.id === account?.id}
              className="text-xs"
            >
              {t('people.erasure')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('people.title')} subtitle={t('people.subtitle')} />

      <div className="card">
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={t('common.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <Select
            aria-label={t('people.role')}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as PersonRole | 'all');
              setPage(1);
            }}
          >
            <option value="all">{t('common.all')}</option>
            <option value="athlete">{t('people.roleAthlete')}</option>
            <option value="coach">{t('people.roleCoach')}</option>
            <option value="parent">{t('people.roleParent')}</option>
          </Select>

          <Select
            aria-label={t('people.activity')}
            value={activity}
            onChange={(event) => {
              setActivity(event.target.value as ActivityBand | '');
              setPage(1);
            }}
          >
            <option value="">{t('common.all')}</option>
            <option value="active">{t('activity.active')}</option>
            <option value="lapsing">{t('activity.lapsing')}</option>
            <option value="dormant">{t('activity.dormant')}</option>
            <option value="unknown">{t('activity.unknown')}</option>
          </Select>

          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />

          {filtered ? (
            <Button
              onClick={() => {
                setRole('all');
                setSearch('');
                setActivity('');
                setPage(1);
              }}
            >
              {t('common.reset')}
            </Button>
          ) : null}
        </div>

        <div className="border-b border-line px-3 py-2">
          <ActivityLegend />
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={7} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Users className="h-8 w-8" />}
            title={t('people.emptyTitle')}
            body={t('people.emptyBody')}
          />
        ) : (
          <>
            <DataTable
              caption={t('people.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(person) => person.id}
              sort={sort}
              order={order}
              onSort={(key) => {
                toggle(key);
                setPage(1);
              }}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>

      {/*
        Says outright why coach rows are half empty. Without it the reader
        assumes the dashboard is broken rather than that the data was never
        collected.
      */}
      <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('people.noProfileHint')}
      </p>

      {promoting ? (
        <Modal
          open
          onClose={() => setPromoting(null)}
          title={t(
            promoting.is_super_admin ? 'people.revokeAdminTitle' : 'people.grantAdminTitle',
          )}
          footer={
            <>
              <Button onClick={() => setPromoting(null)}>{t('common.cancel')}</Button>
              <Button
                variant={promoting.is_super_admin ? 'danger' : 'primary'}
                onClick={() => toggleAdmin(promoting)}
              >
                {t(promoting.is_super_admin ? 'people.revokeAdmin' : 'people.grantAdmin')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">
            {t(
              promoting.is_super_admin ? 'people.revokeAdminBody' : 'people.grantAdminBody',
              { name: promoting.real_name || promoting.display_name, email: promoting.email },
            )}
          </p>
        </Modal>
      ) : null}

      {closing ? (
        <Modal
          open
          onClose={() => {
            setClosing(null);
            setTypedEmail('');
          }}
          title={t('people.erasureTitle', { name: closing.real_name || closing.display_name })}
          footer={
            <>
              <Button
                onClick={() => {
                  setClosing(null);
                  setTypedEmail('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={typedEmail.trim().toLowerCase() !== closing.email.toLowerCase()}
                onClick={() => closeAccount(closing)}
              >
                {t('people.erasureConfirm')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">{t('people.erasureBody')}</p>
          <Field label={t('people.erasureType', { email: closing.email })}>
            {(id) => (
              <TextInput
                id={id}
                value={typedEmail}
                autoComplete="off"
                onChange={(event) => setTypedEmail(event.target.value)}
              />
            )}
          </Field>
        </Modal>
      ) : null}

      {editing ? (
        <BaseDataDialog
          person={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('people.baseDataSaved'));
            reload();
          }}
        />
      ) : null}

      {assigning ? (
        <TeamDialog
          person={assigning}
          onClose={() => setAssigning(null)}
          onChanged={(person) => {
            setAssigning(person);
            reload();
          }}
        />
      ) : null}

      {subscribing ? (
        <SubscriptionDialog
          person={subscribing}
          onClose={() => setSubscribing(null)}
          onSaved={() => {
            setSubscribing(null);
            toast.success(t('people.subscriptionSaved'));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Row 103: what this person is entitled to, changed by hand and in both
 * directions.
 *
 * Only the status, because that is the whole of the question here. Plan and
 * amount say what somebody is billed, which belongs on the page about money
 * and would be a second place to get it wrong.
 *
 * The note is not sent at all rather than sent empty: a redeemed code writes
 * into it what the code was handed out for, and taking the VIP away should
 * not also take away the record of why it was given.
 */
function SubscriptionDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Person;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState<SubscriptionStatus>(person.subscription_status);
  const [busy, setBusy] = useState(false);

  const keepsPlan = status !== 'none' && status !== 'vip';

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/subscriptions/${person.id}`, {
        method: 'PUT',
        body: {
          status,
          plan: keepsPlan ? person.subscription_plan : null,
        },
      });
      onSaved();
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.subscriptionTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.subscriptionHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={status === person.subscription_status}
            onClick={save}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('finance.status')}>
        {(id) => (
          <Select
            id={id}
            className="w-full"
            value={status}
            onChange={(event) => setStatus(event.target.value as SubscriptionStatus)}
          >
            {(['none', 'trialing', 'active', 'past_due', 'canceled', 'vip'] as const).map(
              (value) => (
                <option key={value} value={value}>
                  {t(`subscription.${value}`)}
                </option>
              ),
            )}
          </Select>
        )}
      </Field>
    </Modal>
  );
}

/**
 * Row 110: a VIP joins a team and leaves it again, and nothing is charged.
 *
 * Stays open across each change so somebody sorting out one person's teams
 * does not have to find the row again between two of them.
 */
function TeamDialog({
  person,
  onClose,
  onChanged,
}: {
  person: Person;
  onClose: () => void;
  onChanged: (person: Person) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState('');

  const { data: teams } = useResource<AdminTeam[]>('/api/v1/admin/teams', {}, []);
  const available = useMemo(
    () =>
      (teams ?? []).filter(
        (team) => !person.teams.some((joined) => joined.id === team.id),
      ),
    [teams, person.teams],
  );

  async function change(run: () => Promise<unknown>, message: string) {
    setBusy(true);
    try {
      const updated = (await run()) as Person;
      onChanged(updated);
      setPicked('');
      toast.success(message);
    } catch {
      toast.failure(t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.teamTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.teamHint')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-medium text-ink-muted">
            {t('people.teamsCurrent')}
          </div>
          {person.teams.length ? (
            <ul className="space-y-2">
              {person.teams.map((team) => (
                <li
                  key={team.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2"
                >
                  <span className="truncate text-sm text-ink">{team.name}</span>
                  <Button
                    className="text-xs"
                    disabled={busy}
                    onClick={() =>
                      change(
                        () =>
                          request(
                            `/api/v1/admin/vips/${person.id}/teams/${team.id}`,
                            { method: 'DELETE' },
                          ),
                        t('people.teamRemoved', { team: team.name }),
                      )
                    }
                  >
                    {t('people.teamRemove')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-subtle">{t('people.teamsNone')}</p>
          )}
        </div>

        <Field label={t('people.teamAdd')}>
          {(id) => (
            <div className="flex gap-2">
              <Select
                id={id}
                className="w-full"
                value={picked}
                onChange={(event) => setPicked(event.target.value)}
              >
                <option value="">{t('people.teamPick')}</option>
                {available.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.club_name ? `${team.club_name} · ${team.name}` : team.name}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                loading={busy}
                disabled={!picked}
                onClick={() =>
                  change(
                    () =>
                      request(`/api/v1/admin/vips/${person.id}/teams`, {
                        method: 'POST',
                        body: { team_id: picked },
                      }),
                    t('people.teamAdded'),
                  )
                }
              >
                {t('people.teamAssign')}
              </Button>
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
}

function BaseDataDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Person;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [realName, setRealName] = useState(person.real_name ?? '');
  const [anonymous, setAnonymous] = useState(person.board_anonymous);
  const [birthDate, setBirthDate] = useState(person.birth_date ?? '');
  const [gender, setGender] = useState(person.gender ?? '');
  const [location, setLocation] = useState(person.location ?? '');
  const [country, setCountry] = useState(person.country ?? '');
  const [nationality, setNationality] = useState(person.nationality ?? '');

  const { data: codes } = useResource<string[]>('/api/v1/admin/countries', {}, []);
  // Sorted by the name the reader sees, not by the code behind it: by code the
  // list opens Andorra, United Arab Emirates, Afghanistan.
  const countries = useMemo(() => {
    const language = i18n.language;
    return (codes ?? [])
      .map((code) => ({ code, name: countryName(code, language) ?? code }))
      .sort((a, b) => a.name.localeCompare(b.name, language));
  }, [codes, i18n.language]);

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/people/${person.id}/profile`, {
        method: 'PATCH',
        body: {
          real_name: realName.trim() || null,
          birth_date: birthDate || null,
          gender: gender || null,
          location: location.trim() || null,
          country: country || null,
          nationality: nationality || null,
          board_anonymous: anonymous,
        },
      });
      onSaved();
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.baseDataTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.baseDataHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('people.realName')}>
          {(id) => (
            <TextInput
              id={id}
              value={realName}
              maxLength={100}
              onChange={(event) => setRealName(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('people.birthDate')}>
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('people.gender')}>
          {(id) => (
            <Select
              id={id}
              className="w-full"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
            >
              <option value="">{t('common.none')}</option>
              <option value="male">{t('people.genderMale')}</option>
              <option value="female">{t('people.genderFemale')}</option>
              <option value="other">{t('people.genderOther')}</option>
            </Select>
          )}
        </Field>

        <Field label={t('people.residence')} hint={t('people.residenceHint')}>
          {(id, describedBy) => (
            <CountrySelect
              id={id}
              describedBy={describedBy}
              value={country}
              onChange={setCountry}
              options={countries}
              noneLabel={t('common.none')}
            />
          )}
        </Field>

        <Field label={t('people.nationality')} hint={t('people.nationalityHint')}>
          {(id, describedBy) => (
            <CountrySelect
              id={id}
              describedBy={describedBy}
              value={nationality}
              onChange={setNationality}
              options={countries}
              noneLabel={t('common.none')}
            />
          )}
        </Field>

        <Field label={t('people.location')}>
          {(id) => (
            <TextInput
              id={id}
              value={location}
              maxLength={200}
              onChange={(event) => setLocation(event.target.value)}
            />
          )}
        </Field>

        {person.role === 'athlete' ? (
          /* Row 117: the objection is the player's, but the person asking for
             it is usually a parent on the phone. */
          <Field label={t('people.boardAnonymous')} hint={t('people.boardAnonymousHint')}>
            {() => (
              <Toggle
                checked={anonymous}
                onChange={setAnonymous}
                label={t('people.boardAnonymous')}
              />
            )}
          </Field>
        ) : null}

        {person.role === 'coach' ? (
          <p className="text-xs text-ink-subtle">{t('people.positionAthleteOnly')}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function CountrySelect({
  id,
  describedBy,
  value,
  onChange,
  options,
  noneLabel,
}: {
  id: string;
  describedBy?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ code: string; name: string }>;
  noneLabel: string;
}) {
  return (
    <Select
      id={id}
      aria-describedby={describedBy}
      className="w-full"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{noneLabel}</option>
      {/*
        A country already on the row but missing from the list still has to
        show, or opening the form and saving it would quietly clear it.
      */}
      {value && !options.some((option) => option.code === value) ? (
        <option value={value}>{value}</option>
      ) : null}
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {option.name}
        </option>
      ))}
    </Select>
  );
}

function Missing() {
  return (
    <span aria-hidden className="text-ink-subtle">
      —
    </span>
  );
}

function cap(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
